// Moved out of pages/CanvasUI.tsx unchanged (Threads split).
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Bug, ChevronDown, User, Search, ArrowUpRight, Copy as CopyIcon, Maximize2 } from 'lucide-react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import vscDarkPlus from 'react-syntax-highlighter/dist/esm/styles/prism/vsc-dark-plus';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { isSystemAdminRole } from '@/lib/roles';
import { formatTraceCurrency } from '@/components/execution-trace/ExecutionTrace';
import type { AgenticExecutionMetadata, AgenticToolTiming } from '@shared/agentic-execution';

export interface AdminDebugPanelProps {
  metadata?: Record<string, any>;
  messageId: string | number;
}

export type ToolCallStatus = 'success' | 'error' | 'unknown';

export interface DebugToolCall {
  id: string;
  name: string;
  status: ToolCallStatus;
  argumentsText?: string;
  resultText?: string;
  latencyLabel?: string;
  toolType?: string;
  startTime?: string;
  endTime?: string;
  raw: Record<string, any>;
  errorMessage?: string;
}

export const formatDuration = (ms?: number | null) => {
  if (!Number.isFinite(ms as number)) return 'N/A';
  if (!ms || Math.abs(ms) < 1000) return `${Math.round(ms ?? 0)} ms`;
  return `${((ms as number) / 1000).toFixed(2)} s`;
};

export const parseNumeric = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const safeJsonStringify = (value: any) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (err) {
    return typeof value === 'string' ? value : String(value);
  }
};

export const normalizeString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

/**
 * Parses a "provider:model" string (e.g., "ANTHROPIC:claude-haiku-4-5") into parts.
 * Used as a fallback when explicit provider/model fields are missing.
 */
export const parseProviderModelString = (
  value: unknown,
): { provider: string | null; model: string | null } => {
  const str = normalizeString(value);
  if (!str) {
    return { provider: null, model: null };
  }
  const colonIndex = str.indexOf(':');
  if (colonIndex > 0 && colonIndex < str.length - 1) {
    return {
      provider: str.slice(0, colonIndex).trim() || null,
      model: str.slice(colonIndex + 1).trim() || null,
    };
  }
  // No colon found; treat the whole string as model
  return { provider: null, model: str };
};

export const computeResponseTiming = (metadata: Record<string, any>) => {
  // V4 agentic timings fallback source
  const agenticTimings = metadata?.agenticExecution?.timings;

  const candidates: Array<number | null> = [
    parseNumeric(metadata?.responseTimeMs),
    parseNumeric(metadata?.responseTimeMS),
    parseNumeric(metadata?.responseTime),
    parseNumeric(metadata?.latencyMs),
    parseNumeric(metadata?.latency),
    parseNumeric(metadata?.durationMs),
    parseNumeric(metadata?.totalDurationMs),
    parseNumeric(metadata?.metrics?.responseTimeMs),
    parseNumeric(metadata?.metrics?.latencyMs),
    // V4 agentic execution timing fallbacks
    parseNumeric(agenticTimings?.totalMs),
    parseNumeric(agenticTimings?.orchestratorMs),
  ];

  const msCandidate = candidates.find((value) => typeof value === 'number' && value >= 0);
  if (typeof msCandidate === 'number') {
    return { label: formatDuration(msCandidate), ms: msCandidate };
  }

  // Try top-level timestamps first
  const startIso = normalizeString(
    metadata?.startTime || metadata?.startedAt || metadata?.requestStartedAt,
  );
  const endIso = normalizeString(
    metadata?.endTime || metadata?.completedAt || metadata?.responseCompletedAt,
  );

  if (startIso && endIso) {
    try {
      const diff = new Date(endIso).getTime() - new Date(startIso).getTime();
      if (Number.isFinite(diff) && diff >= 0) {
        return { label: formatDuration(diff), ms: diff };
      }
    } catch {
      // Ignore parsing errors and fall through to V4 fallback.
    }
  }

  // V4 agentic execution timestamp fallback
  const v4StartIso = normalizeString(agenticTimings?.startedAt);
  const v4EndIso = normalizeString(agenticTimings?.completedAt);

  if (v4StartIso && v4EndIso) {
    try {
      const diff = new Date(v4EndIso).getTime() - new Date(v4StartIso).getTime();
      if (Number.isFinite(diff) && diff >= 0) {
        return { label: formatDuration(diff), ms: diff };
      }
    } catch {
      // Ignore parsing errors and fall through to default value.
    }
  }

  return { label: 'N/A', ms: null };
};

export const extractToolCalls = (metadata: Record<string, any>): DebugToolCall[] => {
  const results: DebugToolCall[] = [];
  const seenIds = new Set<string>();

  const agenticCalls: any[] = Array.isArray(metadata?.agenticExecution?.toolCalls)
    ? metadata.agenticExecution.toolCalls
    : [];

  agenticCalls.forEach((call, index) => {
    const id =
      normalizeString(call?.id) ??
      `tool-call-${index}`;
    if (seenIds.has(id)) {
      return;
    }
    seenIds.add(id);
    const status: ToolCallStatus =
      call?.status === 'error'
        ? 'error'
        : call?.status === 'success'
          ? 'success'
          : 'unknown';
    results.push({
      id,
      name: normalizeString(call?.tool) ?? `Tool Call ${index + 1}`,
      status,
      argumentsText: call?.parameters
        ? safeJsonStringify(call.parameters)
        : undefined,
      resultText: normalizeString(call?.resultSnippet) ?? undefined,
      latencyLabel:
        typeof call?.durationMs === 'number'
          ? formatDuration(call.durationMs)
          : undefined,
      toolType: undefined,
      startTime: normalizeString(call?.startedAt) ?? undefined,
      endTime: normalizeString(call?.completedAt) ?? undefined,
      raw: call ?? {},
      errorMessage: normalizeString(call?.error) ?? undefined,
    });
  });

  const legacySources: any[] = Array.isArray(metadata?.mcpToolCalls)
    ? metadata.mcpToolCalls
    : !agenticCalls.length && Array.isArray(metadata?.toolCalls)
      ? metadata.toolCalls
      : [];

  legacySources.forEach((call, index) => {
    const rawArguments = call?.function?.arguments ?? call?.arguments ?? null;
    let argumentsText: string | undefined;
    if (rawArguments !== null && rawArguments !== undefined) {
      if (typeof rawArguments === 'string') {
        try {
          const parsed = JSON.parse(rawArguments);
          argumentsText = safeJsonStringify(parsed);
        } catch {
          argumentsText = rawArguments;
        }
      } else {
        argumentsText = safeJsonStringify(rawArguments);
      }
    }

    const rawResult = call?.result ?? call?.output ?? null;
    const resultText =
      rawResult !== null && rawResult !== undefined
        ? safeJsonStringify(rawResult)
        : undefined;

    const latencyValue = parseNumeric(
      call?.latency ?? call?.durationMs ?? call?.executionTimeMs,
    );

    const status: ToolCallStatus =
      call?.success === false || call?.status === 'error'
        ? 'error'
        : call?.success === true || call?.status === 'success'
          ? 'success'
          : 'unknown';

    const id =
      normalizeString(call?.id) ??
      normalizeString(call?.toolCallId) ??
      `tool-call-legacy-${index}`;
    if (seenIds.has(id)) {
      return;
    }
    seenIds.add(id);

    results.push({
      id,
      name:
        normalizeString(call?.function?.name) ??
        normalizeString(call?.toolName) ??
        normalizeString(call?.name) ??
        `Tool Call ${index + 1}`,
      status,
      argumentsText,
      resultText,
      latencyLabel:
        typeof latencyValue === 'number'
          ? formatDuration(latencyValue)
          : undefined,
      toolType:
        normalizeString(call?.toolType) ?? normalizeString(call?.server) ?? undefined,
      startTime:
        normalizeString(call?.startTime) ??
        normalizeString(call?.startedAt) ??
        undefined,
      endTime:
        normalizeString(call?.endTime) ??
        normalizeString(call?.completedAt) ??
        undefined,
      raw: call ?? {},
      errorMessage:
        normalizeString(call?.error) ??
        normalizeString(call?.result?.error) ??
        undefined,
    });
  });

  return results;
};

export const extractToolsUsed = (metadata: Record<string, any>, toolCalls: DebugToolCall[]) => {
  // 1. Top-level toolsUsed array
  if (Array.isArray(metadata?.toolsUsed)) {
    return metadata.toolsUsed
      .map((tool: any) => (typeof tool === 'string' ? tool : tool?.name))
      .filter((value: any): value is string => typeof value === 'string' && value.trim().length > 0);
  }

  // 2. V4 agenticExecution.toolsUsed
  const agenticToolsUsed = metadata?.agenticExecution?.toolsUsed;
  if (Array.isArray(agenticToolsUsed)) {
    return agenticToolsUsed
      .map((tool: any) => (typeof tool === 'string' ? tool : tool?.name))
      .filter((value: any): value is string => typeof value === 'string' && value.trim().length > 0);
  }

  // 3. V4 agenticExecution.toolSummary.uniqueTools
  const uniqueTools = metadata?.agenticExecution?.toolSummary?.uniqueTools;
  if (Array.isArray(uniqueTools)) {
    return uniqueTools.filter(
      (value: any): value is string => typeof value === 'string' && value.trim().length > 0,
    );
  }

  // 4. Legacy mcpServers
  if (Array.isArray(metadata?.mcpServers)) {
    return metadata.mcpServers
      .map((server: any) => (typeof server === 'string' ? server : server?.name ?? server?.id))
      .filter((value: any): value is string => typeof value === 'string' && value.trim().length > 0);
  }

  // 5. Derive from toolCalls by toolType
  const byToolType = toolCalls
    .map((call) => call.toolType)
    .filter((value): value is string => typeof value === 'string' && value.length > 0);

  return Array.from(new Set(byToolType));
};

export const extractErrors = (metadata: Record<string, any>, toolCalls: DebugToolCall[]) => {
  const errors: string[] = [];

  const metaError = normalizeString(metadata?.error);
  if (metaError) errors.push(metaError);

  if (Array.isArray(metadata?.errors)) {
    metadata.errors.forEach((err: any) => {
      const text = normalizeString(typeof err === 'string' ? err : err?.message ?? err?.detail);
      if (text) errors.push(text);
    });
  }

  toolCalls.forEach((call) => {
    if (call.errorMessage) {
      errors.push(`${call.name}: ${call.errorMessage}`);
    }
  });

  return Array.from(new Set(errors));
};

export const AdminDebugPanel: React.FC<AdminDebugPanelProps> = ({ metadata, messageId }) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const isSystemAdmin = isSystemAdminRole(user?.role);
  if (!isSystemAdmin) {
    return null;
  }

  const meta = useMemo<Record<string, any>>(() => {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      return {};
    }
    return metadata as Record<string, any>;
  }, [metadata]);

  const effectiveMessageId = messageId ?? meta.messageId ?? meta.id;
  const itemValue = useMemo(() => String(effectiveMessageId ?? Math.random()), [effectiveMessageId]);
  const storageKey = `admin-debug-panel:${itemValue}`;

  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(storageKey) === 'open';
  });

  const [showJson, setShowJson] = useState(false);
  const [showErrorRaw, setShowErrorRaw] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isExpanded) {
      sessionStorage.setItem(storageKey, 'open');
    } else {
      sessionStorage.removeItem(storageKey);
    }
  }, [isExpanded, storageKey]);

  const agenticExecution = meta?.agenticExecution as AgenticExecutionMetadata | undefined;

  // Provider derivation with V4 fallbacks
  const provider = useMemo(() => {
    // 1. Legacy top-level fields
    const legacyProvider = normalizeString(
      meta?.provider ?? meta?.finalResponseProvider ?? meta?.modelProvider,
    );
    if (legacyProvider) return legacyProvider;

    // 2. V4 orchestrator.provider
    const orchestratorProvider = normalizeString(meta?.orchestrator?.provider);
    if (orchestratorProvider) return orchestratorProvider;

    // 3. V4 agenticExecution.tokenUsage.provider
    const tokenUsageProvider = normalizeString(agenticExecution?.tokenUsage?.provider);
    if (tokenUsageProvider) return tokenUsageProvider;

    // 4. Parse from orchestratorModel or orchestrator.label (e.g., "ANTHROPIC:claude-haiku-4-5")
    const parsedFromTimings = parseProviderModelString(agenticExecution?.timings?.orchestratorModel);
    if (parsedFromTimings.provider) return parsedFromTimings.provider;

    const parsedFromLabel = parseProviderModelString(meta?.orchestrator?.label);
    if (parsedFromLabel.provider) return parsedFromLabel.provider;

    return null;
  }, [meta, agenticExecution]);

  // Model derivation with V4 fallbacks
  const model = useMemo(() => {
    // 1. Legacy top-level fields
    const legacyModel = normalizeString(
      meta?.model ?? meta?.finalResponseModel ?? meta?.modelName,
    );
    if (legacyModel) return legacyModel;

    // 2. V4 orchestrator.model
    const orchestratorModel = normalizeString(meta?.orchestrator?.model);
    if (orchestratorModel) return orchestratorModel;

    // 3. V4 agenticExecution.tokenUsage.model
    const tokenUsageModel = normalizeString(agenticExecution?.tokenUsage?.model);
    if (tokenUsageModel) return tokenUsageModel;

    // 4. Parse from orchestratorModel or orchestrator.label (e.g., "ANTHROPIC:claude-haiku-4-5")
    const parsedFromTimings = parseProviderModelString(agenticExecution?.timings?.orchestratorModel);
    if (parsedFromTimings.model) return parsedFromTimings.model;

    const parsedFromLabel = parseProviderModelString(meta?.orchestrator?.label);
    if (parsedFromLabel.model) return parsedFromLabel.model;

    return null;
  }, [meta, agenticExecution]);

  const responseTiming = computeResponseTiming(meta);
  const agenticError = (meta?.agenticError ?? agenticExecution?.error ?? null) as
    | AgenticExecutionMetadata['error']
    | null;
  const agenticTimings = agenticExecution?.timings;
  const toolTimings: AgenticToolTiming[] =
    agenticTimings && Array.isArray(agenticTimings.tools) ? agenticTimings.tools : [];
  const v4TokenUsage = agenticExecution?.tokenUsage ?? null;
  const v4Cost = agenticExecution?.cost ?? null;
  const isV4Message = Boolean(v4TokenUsage || Array.isArray(agenticExecution?.toolCalls));
  const toolCalls = useMemo<DebugToolCall[]>(() => extractToolCalls(meta), [meta]);
  const toolsUsed = useMemo<string[]>(() => extractToolsUsed(meta, toolCalls), [meta, toolCalls]);
  const errors = useMemo<string[]>(() => extractErrors(meta, toolCalls), [meta, toolCalls]);
  const rawJson = useMemo(() => safeJsonStringify(meta), [meta]);

  const unsplashInfo = useMemo(() => {
    const info = meta?.unsplash;
    if (!info || typeof info !== 'object') {
      return null;
    }
    const count = parseNumeric(info.count) ?? (Array.isArray(info.images) ? info.images.length : null);
    const total = parseNumeric(info.totalAvailable) ?? parseNumeric(info.total);
    return {
      searchTerms: normalizeString(info.searchTerms) ?? null,
      count,
      total,
      images: Array.isArray(info.images) ? info.images : [],
    };
  }, [meta]);

  const sources = useMemo<any[]>(() => (Array.isArray(meta?.sources) ? meta.sources : []), [meta]);
  const usageStats = useMemo(() => {
    const totals = { totalTokens: 0, promptTokens: 0, completionTokens: 0 };
    const processed = new Set<any>();

    const readNumeric = (source: any, keys: string[]) => {
      if (!source || typeof source !== 'object') return null;
      for (const key of keys) {
        const value = source[key];
        const parsed = parseNumeric(value);
        if (typeof parsed === 'number') {
          return parsed;
        }
      }
      return null;
    };

    const enqueueNested = (usage: any, queue: any[]) => {
      if (!usage || typeof usage !== 'object') return;
      const nestedKeys = ['tokens', 'usage', 'billing', 'details', 'metrics'];
      nestedKeys.forEach((key) => {
        const nested = usage[key];
        if (!nested) return;
        if (Array.isArray(nested)) {
          nested.forEach((item) => {
            if (item && typeof item === 'object') {
              queue.push(item);
            }
          });
        } else if (typeof nested === 'object') {
          queue.push(nested);
        }
      });
    };

    const addUsage = (usage: any) => {
      if (!usage || typeof usage !== 'object' || processed.has(usage)) return;
      processed.add(usage);

      const total = readNumeric(usage, ['totalTokens', 'total_tokens', 'total']);
      const prompt = readNumeric(usage, ['promptTokens', 'prompt_tokens', 'inputTokens', 'input_tokens', 'prompt']);
      const completion = readNumeric(usage, ['completionTokens', 'completion_tokens', 'outputTokens', 'output_tokens', 'completion']);

      if (typeof total === 'number') totals.totalTokens += total;
      if (typeof prompt === 'number') totals.promptTokens += prompt;
      if (typeof completion === 'number') totals.completionTokens += completion;

      const queue: any[] = [];
      enqueueNested(usage, queue);
      while (queue.length > 0) {
        addUsage(queue.pop());
      }
    };

    addUsage(meta?.usage);
    addUsage(meta?.tokens);
    addUsage(meta?.agenticExecution?.tokenUsage);
    addUsage(meta?.tokenUsage);
    addUsage(meta?.result?.usage);
    addUsage(meta?.result?.tokenUsage);

    const usageSources = Array.isArray(meta?.mcpToolCalls)
      ? meta.mcpToolCalls
      : Array.isArray(meta?.toolCalls)
        ? meta.toolCalls
        : [];

    usageSources.forEach((call: any) => {
      addUsage(call?.usage);
      addUsage(call?.metrics);
      addUsage(call?.tokenUsage);
      addUsage(call?.result?.usage);
      addUsage(call?.result?.tokenUsage);
    });

    if (
      totals.totalTokens === 0 &&
      totals.promptTokens === 0 &&
      totals.completionTokens === 0
    ) {
      return null;
    }

    if (totals.totalTokens === 0) {
      totals.totalTokens = totals.promptTokens + totals.completionTokens;
    }

    return totals;
  }, [meta]);

  const toolErrorCount = useMemo(
    () => toolCalls.filter((call) => call.status === 'error').length,
    [toolCalls],
  );

  const summaryProvider = provider ?? 'Unknown provider';
  const summaryModel = model ?? 'Unknown model';
  const toolCount = toolCalls.length || toolsUsed.length || parseNumeric(meta?.totalToolCalls) || 0;
  const toolCountLabel = toolCount ? String(toolCount) : '0';

  const callBreakdown = useMemo(() => {
    const sections: Array<{ title: string; lines: string[] }> = [];
    const orchestrator = meta?.orchestrator;
    if (orchestrator) {
      sections.push({
        title: 'Orchestrator Routing',
        lines: [
          `Tool: ${orchestrator.tool}`,
          `Confidence: ${Math.round((orchestrator.confidence ?? 0) * 100)}%`,
          orchestrator.reasoning ? `Reasoning: ${orchestrator.reasoning}` : null,
        ].filter(Boolean) as string[],
      });
    }

    sections.push({
      title: 'Primary Model Invocation',
      lines: [
        `Provider: ${summaryProvider}`,
        `Model: ${summaryModel}`,
        `Latency: ${responseTiming.label}`,
      ],
    });

    const reportedTotal =
      typeof meta?.totalToolCalls === 'number' ? meta.totalToolCalls : toolCalls.length;
    if (reportedTotal > 0) {
      const successful =
        typeof meta?.successfulToolCalls === 'number'
          ? meta.successfulToolCalls
          : reportedTotal - toolErrorCount;
      sections.push({
        title: 'MCP Tool Calls',
        lines: [
          `Total: ${reportedTotal}`,
          `Successful: ${Math.max(successful, 0)}`,
          `Errors: ${Math.max(reportedTotal - Math.max(successful, 0), 0)}`,
        ],
      });
    }

    if (toolsUsed.length > 0) {
      sections.push({
        title: 'Tools Invoked',
        lines: toolsUsed.map((tool) => `• ${tool}`),
      });
    }

    if (meta?.webContentInjected || meta?.webContentUrl) {
      sections.push({
        title: 'Web Content Context',
        lines: [
          meta?.webContentTitle ? `Title: ${meta.webContentTitle}` : null,
          meta?.webContentUrl ? `URL: ${meta.webContentUrl}` : null,
        ].filter(Boolean) as string[],
      });
    }

    if (meta?.userQuery) {
      sections.push({
        title: 'Sanitized User Query',
        lines: [meta.userQuery],
      });
    }

    if (unsplashInfo) {
      sections.push({
        title: 'Image Search',
        lines: [
          unsplashInfo.searchTerms
            ? `Search terms: ${unsplashInfo.searchTerms}`
            : 'Search terms unavailable',
          `Images returned: ${unsplashInfo.count ?? unsplashInfo.images.length}`,
          unsplashInfo.total ? `Total available: ${unsplashInfo.total}` : null,
        ].filter(Boolean) as string[],
      });
    }

    if (usageStats) {
      sections.push({
        title: 'Token Usage',
        lines: [
          `Total tokens: ${usageStats.totalTokens.toLocaleString()}`,
          `Prompt tokens: ${usageStats.promptTokens.toLocaleString()}`,
          `Completion tokens: ${usageStats.completionTokens.toLocaleString()}`,
        ],
      });
    }

    if (agenticExecution?.cost) {
      sections.push({
        title: 'Estimated Cost',
        lines: [
          formatTraceCurrency(
            agenticExecution.cost.total,
            agenticExecution.cost.currency,
          ),
        ],
      });
    }

    return sections.filter((section) => section.lines.length > 0);
  }, [meta, toolCalls, toolsUsed, toolErrorCount, responseTiming.label, summaryProvider, summaryModel, unsplashInfo, usageStats, agenticExecution?.cost]);

  const hasMetadataKeys = useMemo(() => Object.keys(meta).length > 0, [meta]);
  const showEmptyState =
    !hasMetadataKeys &&
    toolCalls.length === 0 &&
    toolsUsed.length === 0 &&
    errors.length === 0 &&
    !unsplashInfo &&
    sources.length === 0 &&
    !usageStats;

  const handleAccordionChange = (value: string) => {
    setIsExpanded(value === itemValue);
  };

  const handleCopy = async () => {
    const payload = {
      messageId: effectiveMessageId,
      provider: summaryProvider,
      model: summaryModel,
      responseTimeMs: responseTiming.ms,
      toolCount,
      toolsUsed,
      toolCalls: toolCalls.map((call) => call.raw),
      errors,
      metadata: meta,
    };

    try {
      await navigator.clipboard.writeText(safeJsonStringify(payload));
      toast({ description: 'Debug payload copied to clipboard.' });
    } catch (error) {
      console.error('Failed to copy debug payload', error);
      toast({
        variant: 'destructive',
        description: 'Unable to copy debug payload. Please try again.',
      });
    }
  };

  const renderSource = (source: any, index: number) => {
    const rawUrl =
      typeof source === 'string'
        ? source
        : source?.url || source?.uri || source?.link || source?.href || '';
    const url = rawUrl ? String(rawUrl) : '';
    const domain = url
      ? (() => {
          try {
            return new URL(url).hostname;
          } catch {
            return '';
          }
        })()
      : '';
    const title =
      (typeof source === 'object' && (source?.title || source?.name || source?.label)) ||
      domain ||
      `Source ${index + 1}`;
    const snippet =
      (typeof source === 'object' && (source?.snippet || source?.summary || source?.description)) ||
      '';

    return (
      <div
        key={index}
        className="rounded-md border border-gray-200 bg-white p-3 shadow-sm space-y-1 text-xs text-gray-600 break-words"
      >
        <p className="font-semibold text-gray-700">{title}</p>
        {snippet && <p className="text-gray-500 line-clamp-3">{snippet}</p>}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-orange-600 hover:underline"
          >
            {domain || 'Open source'}
            <ArrowUpRight className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  };

  const structuredContent = (
    <div className="space-y-5 text-sm break-words">
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Provider</p>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{summaryProvider}</div>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Model</p>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{summaryModel}</div>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Response Time</p>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{responseTiming.label}</div>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tools Invoked</p>
          <div className="flex flex-wrap gap-1">
            {toolsUsed.length > 0 ? (
              toolsUsed.map((tool) => (
                <Badge key={tool} variant="outline" className="text-xs">
                  {tool}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-gray-500">None</span>
            )}
          </div>
        </div>
      </div>

      {isV4Message && (v4TokenUsage || v4Cost) && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-orange-700 mb-3">
            V4 Token & Cost Summary
          </p>
          <div className="grid gap-4 sm:grid-cols-4">
            {v4TokenUsage && (
              <>
                {typeof v4TokenUsage.inputTokens === 'number' && (
                  <div>
                    <p className="text-xs text-orange-600">Input Tokens</p>
                    <p className="text-lg font-semibold text-orange-900">
                      {v4TokenUsage.inputTokens.toLocaleString()}
                    </p>
                  </div>
                )}
                {typeof v4TokenUsage.outputTokens === 'number' && (
                  <div>
                    <p className="text-xs text-orange-600">Output Tokens</p>
                    <p className="text-lg font-semibold text-orange-900">
                      {v4TokenUsage.outputTokens.toLocaleString()}
                    </p>
                  </div>
                )}
                {typeof v4TokenUsage.totalTokens === 'number' && (
                  <div>
                    <p className="text-xs text-orange-600">Total Tokens</p>
                    <p className="text-lg font-semibold text-orange-900">
                      {v4TokenUsage.totalTokens.toLocaleString()}
                    </p>
                  </div>
                )}
              </>
            )}
            {v4Cost && typeof v4Cost.total === 'number' && (
              <div>
                <p className="text-xs text-orange-600">Estimated Cost</p>
                <p className="text-lg font-semibold text-orange-900">
                  {formatTraceCurrency(v4Cost.total, v4Cost.currency)}
                </p>
              </div>
            )}
          </div>
          {v4TokenUsage && (v4TokenUsage.provider || v4TokenUsage.model) && (
            <p className="mt-2 text-xs text-orange-600">
              {v4TokenUsage.provider ? `Provider: ${v4TokenUsage.provider}` : ''}
              {v4TokenUsage.provider && v4TokenUsage.model ? ' • ' : ''}
              {v4TokenUsage.model ? `Model: ${v4TokenUsage.model}` : ''}
            </p>
          )}
        </div>
      )}

      {agenticTimings && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Execution Timings
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Total latency
              </p>
              <p className="text-sm font-medium text-gray-900">
                {formatDuration(agenticTimings.totalMs ?? null)}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Orchestrator
              </p>
              <p className="text-sm font-medium text-gray-900">
                {formatDuration(agenticTimings.orchestratorMs ?? null)}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Writer
              </p>
              <p className="text-sm font-medium text-gray-900">
                {agenticTimings.writerMs != null
                  ? formatDuration(agenticTimings.writerMs)
                  : 'Not used'}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Writer model
              </p>
              <p className="text-sm font-medium text-gray-900">
                {agenticTimings.writerModel ?? 'Not invoked'}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Orchestrator model
              </p>
              <p className="text-sm font-medium text-gray-900">
                {agenticTimings.orchestratorModel ?? 'Unknown'}
              </p>
            </div>
            {agenticExecution?.requestId && (
              <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Request ID
                </p>
                <p className="font-mono text-xs text-gray-900 break-all">
                  {agenticExecution.requestId}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {agenticExecution?.toolSummary && (
        <div className="space-y-1 text-xs text-gray-600">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Planner Summary
          </p>
          <p>
            {agenticExecution.toolSummary.totalSteps} step(s),{' '}
            {agenticExecution.toolSummary.totalTools} tool(s) used
          </p>
          {agenticExecution.plan?.reasoning && (
            <p className="text-gray-500 italic">
              {agenticExecution.plan.reasoning}
            </p>
          )}
        </div>
      )}

      {toolTimings.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Tool Chain
          </p>
          <div className="space-y-2">
            {toolTimings.map((timing, index) => {
              const status = timing.status ?? 'unknown';
              const statusClass =
                status === 'success'
                  ? 'bg-emerald-100 text-emerald-700'
                  : status === 'failure'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-700';
              return (
                <div
                  key={`${timing.stepId ?? 'step'}:${timing.tool}:${index}`}
                  className="rounded-md border border-gray-200 bg-white p-3 shadow-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{timing.tool}</p>
                      <p className="text-xs text-gray-500">
                        Step {timing.stepId ?? '—'}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={statusClass}>{status}</Badge>
                      <p className="text-xs text-gray-500">
                        {formatDuration(timing.durationMs ?? null)}
                      </p>
                    </div>
                  </div>
                  {timing.cached && (
                    <p className="mt-1 text-[11px] text-gray-500">
                      Served from cache
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {agenticError && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">
            Error Details
          </p>
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <p className="font-semibold">{agenticError.summary.message}</p>
            <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs text-red-800 sm:grid-cols-2">
              <div>
                <dt className="font-semibold uppercase tracking-wide text-[11px]">Code</dt>
                <dd>{agenticError.summary.code ?? 'N/A'}</dd>
              </div>
              <div>
                <dt className="font-semibold uppercase tracking-wide text-[11px]">Tool</dt>
                <dd>{agenticError.summary.tool ?? 'N/A'}</dd>
              </div>
              <div>
                <dt className="font-semibold uppercase tracking-wide text-[11px]">Step</dt>
                <dd>{agenticError.summary.stepId ?? 'N/A'}</dd>
              </div>
              <div>
                <dt className="font-semibold uppercase tracking-wide text-[11px]">
                  Correlation ID
                </dt>
                <dd className="font-mono break-all">
                  {agenticError.correlationId ?? 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="font-semibold uppercase tracking-wide text-[11px]">
                  Request ID
                </dt>
                <dd className="font-mono break-all">
                  {agenticError.requestId ?? 'N/A'}
                </dd>
              </div>
            </dl>
            {agenticError.raw && (
              <div className="mt-3 space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setShowErrorRaw((prev) => !prev)}
                >
                  {showErrorRaw ? 'Hide raw payload' : 'Show raw payload'}
                </Button>
                {showErrorRaw && (
                  <ScrollArea className="max-h-60 rounded border border-red-200 bg-white">
                    <pre className="whitespace-pre-wrap p-3 text-xs text-gray-800">
                      {safeJsonStringify(agenticError.raw)}
                    </pre>
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {callBreakdown.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Call Breakdown</p>
          <div className="space-y-2">
            {callBreakdown.map((section) => (
              <div
                key={section.title}
                className="rounded-md border border-gray-200 bg-white p-3 shadow-sm"
              >
                <p className="text-xs font-semibold text-gray-700">{section.title}</p>
                <ul className="mt-1 space-y-1 text-xs text-gray-600">
                  {section.lines.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {usageStats && !isV4Message && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Token Usage</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-gray-600">
            <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-700">Total</p>
              <p>{usageStats.totalTokens.toLocaleString()}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-700">Prompt</p>
              <p>{usageStats.promptTokens.toLocaleString()}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
              <p className="font-semibold text-gray-700">Completion</p>
              <p>{usageStats.completionTokens.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {agenticExecution?.cost && !isV4Message && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Estimated Cost</p>
          <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm text-xs text-gray-600">
            <p className="font-semibold text-gray-700">
              {formatTraceCurrency(
                agenticExecution.cost.total,
                agenticExecution.cost.currency,
              )}
            </p>
            {agenticExecution.cost.currency && (
              <p className="text-[11px] text-gray-500">
                Currency: {agenticExecution.cost.currency}
              </p>
            )}
          </div>
        </div>
      )}

      {sources.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Knowledge Sources ({sources.length})
          </p>
          <div className="space-y-2">
            {sources.slice(0, 5).map((source, idx) => renderSource(source, idx))}
            {sources.length > 5 && (
              <p className="text-[11px] text-gray-500">
                +{sources.length - 5} additional source(s) available in metadata JSON.
              </p>
            )}
          </div>
        </div>
      )}

      {unsplashInfo && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Search (Unsplash)</p>
          <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm space-y-1 text-xs text-gray-600">
            {unsplashInfo.searchTerms && (
              <p>
                <span className="font-semibold text-gray-700">Search terms:</span> {unsplashInfo.searchTerms}
              </p>
            )}
            <p>
              <span className="font-semibold text-gray-700">Images returned:</span>{' '}
              {unsplashInfo.count ?? unsplashInfo.images.length}
              {unsplashInfo.total ? ` (total available: ${unsplashInfo.total})` : ''}
            </p>
            {unsplashInfo.images.length > 0 && (
              <div className="mt-2 space-y-1 border-t border-dashed border-gray-200 pt-2">
                {unsplashInfo.images.slice(0, 4).map((image: any, idx: number) => {
                  const title = image?.description || image?.alt_description || image?.title || `Image ${idx + 1}`;
                  const link = image?.links?.html || image?.full_url || image?.url;
                  return (
                    <div key={image?.id ?? idx} className="flex items-center justify-between gap-2">
                      <span className="flex-1 truncate text-gray-700">{title}</span>
                      {link && (
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 text-orange-600 hover:underline text-xs"
                        >
                          View
                        </a>
                      )}
                    </div>
                  );
                })}
                {unsplashInfo.images.length > 4 && (
                  <p className="text-[11px] text-gray-500">
                    +{unsplashInfo.images.length - 4} more image(s) captured in metadata JSON.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Tool Calls ({toolCalls.length})
          </p>
        </div>
        {toolCalls.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-200 px-3 py-2 text-sm text-gray-500">
            No tool calls logged for this message.
          </div>
        ) : (
          <div className="space-y-3">
            {toolCalls.map((call) => (
              <div key={call.id} className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs font-medium">
                      {call.name}
                    </Badge>
                    {call.toolType && (
                      <Badge variant="outline" className="text-xs">
                        {call.toolType}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {call.latencyLabel && <span>{call.latencyLabel}</span>}
                    <span
                      className={
                        call.status === 'error'
                          ? 'font-semibold text-red-600'
                          : call.status === 'success'
                          ? 'font-semibold text-orange-600'
                          : 'text-gray-500'
                      }
                    >
                      {call.status === 'error' ? 'Error' : call.status === 'success' ? 'Success' : 'Unknown'}
                    </span>
                  </div>
                </div>

                {call.errorMessage && (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {call.errorMessage}
                  </div>
                )}

                {call.argumentsText && (
                  <div className="mt-3">
                    <p className="mb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Arguments
                    </p>
                    <ScrollArea className="max-h-40 rounded border border-gray-100 bg-gray-50">
                      <pre className="whitespace-pre-wrap p-3 text-xs text-gray-700">
                        {call.argumentsText}
                      </pre>
                    </ScrollArea>
                  </div>
                )}

                {call.resultText && (
                  <div className="mt-3">
                    <p className="mb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Result
                    </p>
                    <ScrollArea className="max-h-48 rounded border border-gray-100 bg-gray-50">
                      <pre className="whitespace-pre-wrap p-3 text-xs text-gray-700">
                        {call.resultText}
                      </pre>
                    </ScrollArea>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Errors</p>
          <ul className="space-y-2">
            {errors.map((error) => (
              <li key={error} className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {showEmptyState && (
        <div className="rounded-md border border-dashed border-gray-200 px-3 py-2 text-sm text-gray-500">
          No debug metadata is available for this response.
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Full JSON</p>
          <Button
            onClick={() => setShowJson((prev) => !prev)}
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
          >
            {showJson ? 'Hide JSON' : 'Show JSON'}
          </Button>
        </div>
        {showJson && (
          <ScrollArea className="h-64 rounded border border-gray-200 bg-black pr-4 pb-4 max-w-full">
            <div className="min-w-0 max-w-full overflow-auto">
              <SyntaxHighlighter
                language="json"
                style={vscDarkPlus as any}
                customStyle={{ margin: 0, padding: '12px', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                wrapLongLines
              >
                {rawJson}
              </SyntaxHighlighter>
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );

  return (
    <div className="mt-4">
      <Accordion
        type="single"
        collapsible
        value={isExpanded ? itemValue : ''}
        onValueChange={handleAccordionChange}
        className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm"
      >
        <AccordionPrimitive.Item value={itemValue} className="border-0">
          <div className="flex items-stretch gap-1">
            <AccordionPrimitive.Trigger asChild>
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center justify-between gap-3 px-3 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                {/* AR-316: this row wraps below `sm` rather than shrinking. On a
                    phone the two `truncate` fields were squeezed to 0px wide,
                    leaving a line of bare "|" separators; the separators are
                    hidden there and each field takes its own line instead. */}
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs sm:flex-nowrap">
                  <Bug className="h-4 w-4 shrink-0 text-orange-600" aria-hidden="true" />
                  <span className="shrink-0 font-semibold text-gray-800">AI Debug</span>
                  <span className="hidden text-gray-500 sm:inline">|</span>
                  <span className="text-gray-600 sm:truncate">Provider: {summaryProvider}</span>
                  <span className="hidden text-gray-500 sm:inline">|</span>
                  <span className="text-gray-600 sm:truncate">Model: {summaryModel}</span>
                  <span className="hidden text-gray-500 sm:inline">|</span>
                  <span className="shrink-0 text-gray-600">Time: {responseTiming.label}</span>
                  <span className="hidden text-gray-500 sm:inline">|</span>
                  <span className="shrink-0 text-gray-600">Tools: {toolCountLabel}</span>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
            </AccordionPrimitive.Trigger>
            <div className="flex items-center gap-1 pr-3">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-gray-600"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCopy();
                      }}
                    >
                      <CopyIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Copy debug info</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Copy debug info</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-600"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Maximize2 className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">Open fullscreen debug view</span>
                        </Button>
                      </DialogTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Fullscreen</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <DialogContent className="max-h-[95vh] h-[90vh] w-[96vw] max-w-[1400px] sm:max-w-[90vw] lg:max-w-screen-2xl overflow-hidden p-6">
                  <DialogHeader>
                    <DialogTitle>AI Debug Details</DialogTitle>
                    <DialogFooter className="mt-2 justify-between gap-2 text-sm text-muted-foreground sm:flex-row">
                      <div className="flex flex-wrap items-center gap-2 text-foreground">
                        <Badge variant="secondary" className="text-xs">{summaryProvider}</Badge>
                        <Badge variant="outline" className="text-xs">{summaryModel}</Badge>
                        <Badge variant="outline" className="text-xs">Time: {responseTiming.label}</Badge>
                        <Badge variant="outline" className="text-xs">Tools: {toolCountLabel}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleCopy}>
                          <CopyIcon className="mr-2 h-3.5 w-3.5" />
                          Copy Debug Info
                        </Button>
                      </div>
                    </DialogFooter>
                  </DialogHeader>

                  <Tabs defaultValue="overview" className="flex h-full flex-1 flex-col">
                    <TabsList className="w-fit">
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                      <TabsTrigger value="json">Raw JSON</TabsTrigger>
                    </TabsList>
                    <TabsContent value="overview" className="flex-1 min-h-0 overflow-hidden">
                      <ScrollArea className="h-[75vh] pr-6 pb-6 max-w-full">
                        {structuredContent}
                      </ScrollArea>
                    </TabsContent>
                    <TabsContent value="json" className="flex-1 min-h-0 overflow-hidden">
                      <ScrollArea className="h-[75vh] rounded border border-gray-200 bg-black pr-6 pb-6 max-w-full">
                        <div className="min-w-0 max-w-full overflow-auto">
                          <SyntaxHighlighter
                            language="json"
                            style={vscDarkPlus as any}
                            customStyle={{ margin: 0, padding: '16px', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                            wrapLongLines
                          >
                            {rawJson}
                          </SyntaxHighlighter>
                        </div>
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <AccordionPrimitive.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className="border-t border-gray-100 bg-gray-50 px-3 py-4">{structuredContent}</div>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      </Accordion>
    </div>
  );
};
